import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, ChevronDown, ChevronsRight, Dot } from "lucide-react";
import SubTable from "../Table/SubTable";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/authSlice";
import {
  useGetCompanySubscriptionsQuery,
  useGetAllSubscriptionPlansQuery,
  useCancelSubscriptionMutation,
} from "../../redux/services/subscription";
import CancelSubscriptionModal from "../pages/Users/modal/CancelSubscriptionModal";
import ComparePlans from "./_components/ComparePlans";
import FreePlanAlert from "./_components/FreePlanAlert";
import { usePermission } from "../../utils/permissions";
import { splitPlanFeatures } from "../../utils/planFeatures";
import { toast } from "sonner";

export interface ContactInfoFormProps {
  type: "company" | "individual";
}

const starter = {
  features: ["Branch Management", "Chat Support", "Wallet"],
    // limits: ["Up to 1 branches", "Single user", "Up to 3 services"],
  // Fallback only — matches the backend's Starter limits (1 service).
  limits: ["Up to 2 users", "1 branch", "1 service", "Up to 2 fleets"],
};
const growth = {
  features: ["Multi Branch Visibility", "Chat Support", "Activity Tracking"],
  limits: ["Up to 3 branches", "Up to 5 users", "Up to 10 services"],
};
const business = {
  features: ["Multi Branch Visibility", "Activity Tracking", "Analytics and Reporting"],
  limits: ["Up to 10 branches", "Unlimited Services & Users", "Full Reporting"],
};

const isValidId = (id?: string | null): id is string =>
  typeof id === "string" &&
  id.trim() !== "" &&
  id !== "undefined" &&
  id !== "null";

const Subscription: React.FC<ContactInfoFormProps> = ({ type }) => {
  const user = useSelector(selectUser);
  const userCompanyId = user?.company?.id;
  const companyId = isValidId(userCompanyId) ? userCompanyId : undefined;

  const { can } = usePermission();
  const canView = can("view subscription");
  const canCancel = can("cancel subscription");
  const canRenew = can("renew subscription");
  const canUpgrade = can("upgrade subscription");

  const [billingPage, setBillingPage] = useState(1);

  const { data, isLoading, error } = useGetCompanySubscriptionsQuery(
    { companyId: companyId ?? "", page: billingPage },
    { skip: !companyId || !canView }
  );
  console.log("📊 Subscription data:", data);

  useGetAllSubscriptionPlansQuery();

  const [cancelSubscription, { isLoading: cancelLoading }] = useCancelSubscriptionMutation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchParams] = useSearchParams();
  // ?compare=1 (and optionally &plan=<id>) opens the plan comparison directly,
  // e.g. from a "plan limit reached" message.
  const compareParam = searchParams.get("compare") === "1";
  const suggestedPlanId = searchParams.get("plan") ?? undefined;
  const [showComparison, setShowComparison] = useState(compareParam);
  useEffect(() => {
    if (compareParam) setShowComparison(true);
  }, [compareParam, suggestedPlanId]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [freePlanAlert, setFreePlanAlert] = useState(true);
  const [viewHistory, setViewHistory] = useState(false);

  if (!canView) return <p>You don't have permission to view subscription details.</p>;
  if (!companyId) return <p>No company found for this user.</p>;
  if (isLoading) return <p>Loading subscriptions...</p>;
  if (error) return <p>Error loading subscriptions.</p>;

  const activeSubscription = data?.subscription_plan;
  const subStatus = data?.subscription?.status;
  const isUpgradeAction = subStatus === "active" || activeSubscription?.type === "trial";
  const canPerformPlanAction = isUpgradeAction ? canUpgrade : canRenew;

  const subscriptionId =
    data?.subscription?.id ??
    data?.company_subscription_id ??
    data?.subscription_id ??
    data?.data?.[0]?.id ??
    data?.id ??
    undefined;

  const activeClr =
    subStatus === "active"
      ? "bg-[#27ae5f45] text-[#26A65D] border-[#26A65D]"
      : subStatus === "canceled"
      ? "bg-yellow-300 text-gray-700 border-yellow-300"
      : "bg-red-300 text-gray-700 border-gray-300"

  // The plan's own features from the API: limit lines ("Up to 2 users") plus
  // feature keys. Older responses without them fall back to the fixed lists.
  // The free trial has the same limits as Individual Starter.
  const fallbackPlan =
    activeSubscription?.name === "Individual Starter" ||
    activeSubscription?.name === "Individual Trial"
      ? starter
      : activeSubscription?.name === "Individual Growth"
      ? growth
      : business;
  const apiPlan = splitPlanFeatures(activeSubscription?.features);
  const hasApiFeatures = apiPlan.limits.length > 0 || apiPlan.features.length > 0;
  const currentPlanFeatures = hasApiFeatures ? apiPlan.features : fallbackPlan.features;
  const currentPlanLimits = hasApiFeatures ? apiPlan.limits : fallbackPlan.limits;

  const planNameLower = activeSubscription?.name
    ? activeSubscription.name.toLowerCase()
    : "";

  const planClr =
    planNameLower === "individual trial"
      ? "bg-[#0241a73b] text-[#0241a7] border-[#0241a7]"
      : planNameLower === "individual starter"
      ? "bg-[#14b8a533] text-[#14B8A6] border-[#14B8A6]"
      : planNameLower === "individual growth"
      ? "bg-[#1E3A8A30] text-[#1E3A8A] border-[#1E3A8A]"
      : planNameLower === "individual business"
      ? "bg-[#e67e2230] text-[#e67e22] border-[#e67e22]"
      : "bg-gray-300 text-gray-500 border-gray-300";

  const nxtBillingDate = data?.next_billing_date
    ? new Date(data.next_billing_date).toLocaleDateString()
    : "N/A";

  const billingCycle = data?.subscription_plan?.billing_cycle
    ? data.subscription_plan.billing_cycle
        .split(" ")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "Monthly";

  const billingMethod = activeSubscription?.price || "N/A";

  const billingHistory =
    data?.data?.map((sub: any) => ({
      id: sub.id,
      date: sub.created_at,
      invoice: sub.payment_reference,
      plan: sub.subscription_plan,
      amount: sub.amount,
      status: sub.status,
      transaction_reference: sub.transaction_reference,
      payment_reference: sub.payment_reference,
    })) || [];

  const billingCurrentPage =
    data?.data?.current_page ?? data?.meta?.current_page ?? billingPage;
  const billingLastPage =
    data?.data?.last_page ?? data?.meta?.last_page ?? 1;
  const billingPageSize =
    data?.data?.per_page ?? data?.meta?.per_page ?? 10;
  const billingTotal = data?.data?.total ?? data?.meta?.total;

  const plan = activeSubscription?.subscription_plan
    ? { name: activeSubscription.subscription_plan, status: activeSubscription.status }
    : { name: "No plan", status: "N/A" };

  const handleRowClick = (row: any) => {
    setSelectedLog(row);
    setDrawerOpen(true);
  };

  const handleCancelSubscription = () => {
    if (!canCancel) {
      toast.error("You don't have permission to cancel the subscription");
      setDropdownOpen(false);
      return;
    }
    setDropdownOpen(false);
    setCancelModalOpen(true);
  };

  const confirmCancelSubscription = async (reason: string) => {
    if (!canCancel) {
      toast.error("You don't have permission to cancel the subscription");
      return;
    }
    if (!subscriptionId) {
      console.error("❌ No subscriptionId found. data:", data);
      return;
    }
    try {
      await cancelSubscription({ subscriptionId, reason }).unwrap();
      setCancelModalOpen(false);
      // Auto refresh the page after successful cancellation
      window.location.reload();
    } catch (err) {
      console.error("❌ Cancel subscription failed:", err);
    }
  };

  const handleComparePlans = () => {
    setShowComparison(true);
    setDropdownOpen(false);
  };

  if (showComparison) {
    return (
      <ComparePlans
        setShowComparison={setShowComparison}
        activePlan={activeSubscription?.name}
        cycle={billingCycle}
        subStatus={subStatus}
        companyId={companyId}
        subscriptionId={subscriptionId}
        suggestedPlanId={suggestedPlanId}
      />
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 mb-4">
        <div className="flex-1 min-h-[1px] min-w-0">
          {data?.is_trial &&
            freePlanAlert &&
            subStatus === "active" &&
            (data?.days_remaining ?? 0) > 0 && (
              <FreePlanAlert
                setFreePlanAlert={setFreePlanAlert}
                trialEndDays={data.days_remaining}
              />
            )}
        </div>
        <div className="relative self-end sm:self-auto">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 bg-[#1E3A8A] text-white px-4 py-2 rounded-lg shadow hover:bg-[#1E40AF] whitespace-nowrap"
          >
            Manage Subscription
            <ChevronDown size={16} />
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <button
                onClick={handleCancelSubscription}
                disabled={!canCancel}
                title={!canCancel ? "You don't have permission to cancel the subscription" : undefined}
                className={`block w-full text-left px-4 py-2 text-sm ${
                  canCancel ? "hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"
                }`}
              >
                Cancel Subscription
              </button>
              <button
                onClick={handleComparePlans}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
              >
                Compare Plans
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6 bg-gray-200 rounded-xl">
        <div className="flex justify-between items-center">
          <p className="text-lg">Status</p>
          <span className={`px-2 py-1 text-xs border-[0.5px] rounded-full ${activeClr}`}>
            {subStatus === "active" ? "Active" : subStatus === "canceled" ? "Canceled" : "Expired"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-xl shadow p-4 sm:p-6 border border-gray-200">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-medium">
                Plan
                <span className={`px-2 py-[2px] text-xs border-[0.5px] rounded-full ${planClr}`}>
                  {activeSubscription?.name}
                </span>
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mt-4">
              <div>
                <p className="font-semibold text-xs text-gray-400">Features</p>
                <ul>
                  {currentPlanFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Dot size={30} /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-xs text-gray-400">Limits</p>
                <ul>
                  {currentPlanLimits.map((limit, index) => (
                    <li key={index} className="flex items-center">
                      <Dot size={30} /> {limit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-6">
              <button
                onClick={handleComparePlans}
                disabled={!canPerformPlanAction}
                title={
                  !canPerformPlanAction
                    ? `You don't have permission to ${isUpgradeAction ? "upgrade" : "renew"} the subscription`
                    : undefined
                }
                className={`px-4 py-2 rounded-lg text-white ${
                  canPerformPlanAction ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                {isUpgradeAction ? "Upgrade Now" : "Renew Plan"}
              </button>
              <button
                onClick={handleComparePlans}
                className="flex items-center gap-1 hover:text-blue-700"
              >
                All Features <ChevronsRight size={16} />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 sm:p-6 border border-gray-200">
            <h2 className="text-lg font-medium mb-4">Billing Information</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse mb-3">
                <thead>
                  <tr>
                    <th className="pb-2 w-1/2 text-left text-gray-400 text-xs font-bold whitespace-nowrap">
                      Next Billing Date
                    </th>
                    <th className="pb-2 w-1/2 text-left text-gray-400 text-xs font-bold whitespace-nowrap">
                      Billing History
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="whitespace-nowrap">{nxtBillingDate}</td>
                    <td className="text-left">
                      <button
                        onClick={() => setViewHistory(!viewHistory)}
                        className="hover:text-blue-700 hover:border-blue-700 border-b border-gray-700 flex items-center gap-1 text-xs whitespace-nowrap"
                      >
                        {viewHistory ? "Hide History" : "View History"}{" "}
                        {!viewHistory ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="pt-5 pb-2 w-1/2 text-left text-gray-400 text-xs font-bold whitespace-nowrap">
                      Billing Method
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-left whitespace-nowrap">
                      {billingMethod === "0.00"
                        ? "on trial"
                        : `${billingCycle} / ₦${Intl.NumberFormat().format(
                            Number(billingMethod)
                          )}`}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {viewHistory && (
          <div className="bg-white rounded-xl shadow p-4 sm:p-6 border border-gray-200 overflow-x-auto">
            <h2 className="text-lg font-medium mb-4">Billing History</h2>
            <SubTable
              tableData={billingHistory}
              onRowClick={handleRowClick}
              currentPage={billingCurrentPage}
              lastPage={billingLastPage}
              pageSize={billingPageSize}
              total={billingTotal}
              setCurrentPage={setBillingPage}
            />
          </div>
        )}

        <CancelSubscriptionModal
          open={cancelModalOpen}
          planName={plan.name}
          loading={cancelLoading}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={confirmCancelSubscription}
        />
      </div>
    </div>
  );
};

export default Subscription;